import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  GridLegacy as Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

import { useConfirm } from 'material-ui-confirm';
import { Page, Row } from '../../util/Page';

import { validateStagingAction } from '../../../redux/actions/staging';
import getBuildingStaging from '../../../redux/selectors/staging';
import {
  modifyRelations,
  createLocation,
  modifyLocations,
  deleteLocation,
} from '../../../redux/actions/buildings';
import StagingFilterBuilder from '../elements/StagingFilterBuilder';
import { buildIsSelectedFilter, nameFilter } from '../elements/filter/GenericFilters';
import { locationTypeFilter } from '../elements/filter/LocationFilters';
import { equipTypeFilter } from '../elements/filter/EquipFilters';
import BuildingStagingLoader from '../../loaders/BuildingStagingLoader';
import EquipmentLoader from '../../loaders/EquipmentLoader';
import LocationLoader from '../../loaders/LocationLoader';
import { FilterProvider } from '../contexts/FilterContext';
import { LocationSelectorDialog } from '../elements/dialogs/LocationSelectorDialog';
import EditLocationDialog from '../elements/dialogs/EditLocationDialog';
import { EquipmentSelectorDialog } from '../elements/dialogs/EquipmentSelectorDialog';
import LocationRelationList from '../elements/LocationRelationList';
import LocationRelationshipsCard from '../elements/LocationRelationshipsCard';
import { unique } from '../../../utils/collections';

const LocationView = ({
  loaded,
  buildingId,
  locationsByName,
  equipTypes,
  promotedEquipmentById,

  updateContains,
  updateLocationsUncaught,
  createLocationUncaught,
  deleteLocationUncaught,
}) => {
  const buildingEquipmentById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(promotedEquipmentById).filter((entry) => entry[1].buildingId === buildingId)
      ),
    [promotedEquipmentById, buildingId]
  );
  const filterDefs = useMemo(() => [locationTypeFilter, nameFilter], []);
  const allNames = useMemo(
    () =>
      Object.keys(locationsByName).concat(Object.values(promotedEquipmentById).map((e) => e.name)),
    [locationsByName, promotedEquipmentById]
  );

  const selectorFilterDefs = useMemo(
    () => [buildIsSelectedFilter, () => nameFilter, () => equipTypeFilter],
    []
  );

  const confirm = useConfirm();
  const [currentPage, setCurrentPage] = useState(1);
  const [equipmentSelectorOpen, setEquipmentSelectorOpen] = useState(false);
  const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState({});

  const handleAddLocation = () => {
    setEditingLocation({});
    setLocationOpen(true);
  };

  const handleEditLocation = (loc) => {
    setEditingLocation(loc);
    setLocationOpen(true);
  };

  const onEditLocationTargets = (sourceLoc) => {
    if (locationSelectorOpen) return;

    setEditingLocation(sourceLoc);
    setLocationSelectorOpen(true);
  };

  const onEditEquipmentTargets = (sourceLoc) => {
    if (equipmentSelectorOpen) return;

    setEditingLocation(sourceLoc);
    setEquipmentSelectorOpen(true);
  };

  const handleLocationUpdate = (edittedLocation) => {
    if (edittedLocation.name) {
      // rename
      updateLocationsUncaught([{ name: edittedLocation.name, new_name: edittedLocation.new_name }]);
    } else {
      // create
      createLocationUncaught(edittedLocation.new_name, edittedLocation.type);
    }
  };

  const onDeleteLocation = (loc) => {
    confirm({
      content: (
        <>
          <>Are you sure you want to delete location {loc.name}?</>
          <br />
          <br />
          {loc.contains_equipment.length + loc.fed_by_equipment.length > 0 ? (
            <>
              <List sx={{ listStyleType: 'disc' }}>
                The following equipment will be impacted:
                {unique(loc.contains_equipment.concat(loc.fed_by_equipment))
                  .filter(Boolean)
                  .map((equip) => (
                    <ListItem key={equip} sx={{ display: 'list-item', padding: 0 }}>
                      <ListItemText primary={equip} />
                    </ListItem>
                  ))}
              </List>
              <br />
              <br />
            </>
          ) : null}
          {loc.contains_locations.length > 0 || loc.contained_by ? (
            <>
              {' '}
              The following locations will be impacted:
              <List sx={{ listStyleType: 'disc' }}>
                {unique(loc.contains_locations.concat([loc.contained_by]))
                  .filter(Boolean)
                  .map((equip) => (
                    <ListItem key={equip} sx={{ display: 'list-item', padding: 0 }}>
                      <ListItemText primary={equip} />
                    </ListItem>
                  ))}
              </List>
              <br />
              <br />
            </>
          ) : null}
          This action is permanent!
        </>
      ),
    }).then(({ confirmed }) => (confirmed ? deleteLocationUncaught([loc.name]) : null));
  };

  if (!loaded) {
    return (
      <Page>
        <Row>
          <EquipmentLoader key={buildingId} buildingId={buildingId} />
          <LocationLoader key={`ll-${buildingId}`} buildingId={buildingId} />
          <BuildingStagingLoader key={`stg-${buildingId}`} buildingId={buildingId} />
          <LinearProgress color="secondary" className="loading" />
        </Row>
      </Page>
    );
  }

  return (
    <FilterProvider onFilterChange={() => setCurrentPage(1)}>
      <Grid item xs={12}>
        <StagingFilterBuilder filterDefs={filterDefs} />
      </Grid>
      <Grid container item direction="row" xs={12} sx={{ justifyContent: 'flex-end' }}>
        <Button color="success" disabled={!loaded} variant="contained" onClick={handleAddLocation}>
          Add Location
        </Button>
      </Grid>
      <Grid item xs={12}>
        <LocationRelationList
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          locations={Object.values(locationsByName)}
          loading={!loaded}
          renderChild={(l) => (
            <LocationRelationshipsCard
              key={`${l.name}-card`}
              buildingId={buildingId}
              loc={l}
              onEditEquipmentTargets={onEditEquipmentTargets}
              onEditLocationTargets={onEditLocationTargets}
              onUpdateLocation={handleEditLocation}
              onDeleteLocation={onDeleteLocation}
              locationsByName={locationsByName}
            />
          )}
        />
      </Grid>
      <Grid container item direction="row" xs={12} sx={{ justifyContent: 'flex-end' }}>
        <Button color="success" disabled={!loaded} variant="contained" onClick={handleAddLocation}>
          Add Location
        </Button>
      </Grid>
      <FilterProvider>
        <EditLocationDialog
          open={locationOpen}
          buildingId={buildingId}
          onClose={() => setLocationOpen(false)}
          loc={editingLocation}
          allNames={allNames}
          onSubmit={handleLocationUpdate}
        />
      </FilterProvider>
      <FilterProvider>
        <LocationSelectorDialog
          open={locationSelectorOpen}
          onClose={() => setLocationSelectorOpen(false)}
          loading={!loaded}
          buildingId={buildingId}
          editing={editingLocation}
          locationsByName={locationsByName}
          title={`Location ${editingLocation?.name}: Edit CONTAINS Locations`}
          defaultSelection={(loc) => loc?.contains_locations ?? []}
          onSubmit={(selectedTargets) => {
            const locationTargets = editingLocation.contains_locations;

            const newLocations = selectedTargets.filter((name) => !locationTargets.includes(name));

            const newTargets = newLocations.map((tname) => ({
              source_name: editingLocation.name,
              target_name: tname,
            }));

            const removedTargets = locationTargets
              .filter((name) => !selectedTargets.includes(name))
              .map((tname) => ({
                source_name: editingLocation.name,
                target_name: tname,
              }));

            const yankedTargets = newLocations.filter(
              (t) => locationsByName[t].contained_by !== null
            );
            if (yankedTargets.length > 0) {
              return confirm({
                content: (
                  <>
                    The following locations are already contained in other locations. Proceeding
                    will reassign the following to be contained by {editingLocation.name}:
                    <List sx={{ listStyleType: 'disc' }}>
                      {yankedTargets.map((name) => (
                        <ListItem key={name} sx={{ display: 'list-item', padding: 0 }}>
                          <ListItemText primary={name} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                ),
              }).then(({ confirmed }) =>
                confirmed ? updateContains(newTargets, removedTargets) : false
              );
            }
            return updateContains(newTargets, removedTargets);
          }}
        />
        <EquipmentSelectorDialog
          open={equipmentSelectorOpen}
          onClose={() => setEquipmentSelectorOpen(false)}
          loading={!loaded}
          buildingId={buildingId}
          equipTypes={equipTypes}
          editing={editingLocation}
          filterDefBuilders={selectorFilterDefs}
          promotedEquipment={Object.values(buildingEquipmentById)}
          title={`Equipment ${editingLocation?.name}: Edit CONTAINS Equipment`}
          defaultSelection={(loc) => loc?.contains_equipment ?? []}
          onSubmit={(selectedTargets) => {
            const locationTargets = editingLocation.contains_equipment;

            const newEquipment = selectedTargets.filter((name) => !locationTargets.includes(name));
            const newTargets = newEquipment.map((tname) => ({
              source_name: editingLocation.name,
              target_name: tname,
            }));

            const removedTargets = locationTargets
              .filter((name) => !selectedTargets.includes(name))
              .map((tname) => ({
                source_name: editingLocation.name,
                target_name: tname,
              }));

            const yankedTargets = newEquipment.filter((equipName) =>
              Object.values(locationsByName).some((l) => l.contains_equipment.includes(equipName))
            );
            if (yankedTargets.length > 0) {
              return confirm({
                content: (
                  <>
                    The following equipment are already contained in other locations. Proceeding
                    will reassign the following to be contained by {editingLocation.name}:
                    <List sx={{ listStyleType: 'disc' }}>
                      {yankedTargets.map((name) => (
                        <ListItem key={name} sx={{ display: 'list-item', padding: 0 }}>
                          <ListItemText primary={name} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                ),
              }).then(({ confirmed }) =>
                confirmed ? updateContains(newTargets, removedTargets) : false
              );
            }
            return updateContains(newTargets, removedTargets);
          }}
        />
      </FilterProvider>
    </FilterProvider>
  );
};

const mapStateToProps = (state, { buildingId }) => ({
  ...getBuildingStaging(state, buildingId),
  loaded: state.entities.staging.loaded[buildingId] ?? false,
  equipTypes: state.entities.equipTypes.byId,
  promotedEquipmentById: state.entities.equipment.byId ?? {},

  locationsByName: state.entities.buildings.locationsByBuildingIdName[buildingId] ?? {},
  buildingId,
});

const mapDispatchToProps = (dispatch, { buildingId }) => ({
  updateContains: (creates, deletes) =>
    dispatch(modifyRelations(buildingId, 'CONTAINS', creates, deletes)),

  createLocationUncaught: (name, type) =>
    dispatch(createLocation(buildingId, name, type)).then(() =>
      dispatch(validateStagingAction(buildingId))
    ),
  updateLocationsUncaught: (payload) =>
    dispatch(modifyLocations(buildingId, payload)).then(() =>
      dispatch(validateStagingAction(buildingId))
    ),
  deleteLocationUncaught: (name) =>
    dispatch(deleteLocation(buildingId, name)).then(() =>
      dispatch(validateStagingAction(buildingId))
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(LocationView);
